// src/modules/restaurant/pages/respayment/components/PaymentDetailsSection.tsx
import React, { useState } from "react";
import { Divider, CircularProgress } from "@mui/material";
import CommonButtons from "src/shared/components/CommonButtons";
import PaymentHeader from "./../components/paymentSection//PaymentHeader";
import TotalAmountCard from "./../components/paymentSection/TotalAmountCard";
import TipSelector from "./../components/paymentSection/TipSelector";
import PaymentMethodSelector from "./../components/paymentSection/PaymentMethodSelector";
import OrderSummaryCard from "./../components/paymentSection/OrderSummaryCard";
import PaymentCompletedCard from "./../components/paymentSection/PaymentCompletedCard";
import { usePaymentTheme } from "../hooks/usePaymentTheme";
import PaymentIcon from "@mui/icons-material/Payment";
import { OrderList } from "../../../orders/types/orderTypes";

interface Props {
  order: OrderList | null;
  tip: number;
  customTip: string;
  paymentMethod: string;
  guess: number;
  total: number;
  onTipChange: (amount: number) => void;
  onCustomTipChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPayNow: () => void;
  onPaymentMethodChange: (method: string) => void;
  isProcessing: boolean;
}

const PaymentDetailsSection: React.FC<Props> = ({ order, tip, customTip, paymentMethod, guess, total, onTipChange, onCustomTipChange, onPayNow, onPaymentMethodChange, isProcessing }) => {
  const { fontClass, themeBorderColor, themeTextColor, themeRingColor, getBgClass, getLightBgClass } = usePaymentTheme();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const isCompleted = order?.OrderStatus?.toLowerCase() === "completed";
  const finalAmount = (Number(order?.Finalamount ?? 0) + tip).toFixed(2);

  const handlePayNow = async () => {
    // Disable button immediately on click
    setIsButtonDisabled(true);

    try {
      await onPayNow();
    } catch (error) {
      // Re-enable button if there's an error
      setIsButtonDisabled(false);
    }
    // Note: Don't re-enable on success since we navigate away or show success state
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-100 p-1.5 flex flex-col ${fontClass}`}>
      <PaymentHeader getBgClass={getBgClass} themeTextColor={themeTextColor} />
      <TotalAmountCard order={order} guess={guess} getBgClass={getBgClass} isCompleted={isCompleted} finalAmount={finalAmount} />

      <Divider className="my-4 border-gray-200" />
      <TipSelector
        tip={tip}
        customTip={customTip}
        isCompleted={isCompleted}
        onTipChange={onTipChange}
        onCustomTipChange={onCustomTipChange}
        themeBorderColor={themeBorderColor}
        themeTextColor={themeTextColor}
        themeRingColor={themeRingColor}
        getLightBgClass={getLightBgClass}
      />

      <Divider className="my-4 border-gray-200" />
      <PaymentMethodSelector
        paymentMethod={paymentMethod}
        isCompleted={isCompleted}
        onPaymentMethodChange={onPaymentMethodChange}
        themeBorderColor={themeBorderColor}
        themeTextColor={themeTextColor}
        getLightBgClass={getLightBgClass}
      />

      <Divider className="my-4 mb-0.5 border-gray-200" />
      <OrderSummaryCard order={order} tip={tip} total={total} themeTextColor={themeTextColor} getLightBgClass={getLightBgClass} />

      {isCompleted ? (
        <PaymentCompletedCard />
      ) : (
        <CommonButtons
          label={
            isProcessing ? (
              <div className="flex items-center gap-3">
                <CircularProgress size={24} className="text-white" /> Processing...
              </div>
            ) : paymentMethod ? (
              `PAY ${finalAmount}`
            ) : (
              "SELECT PAYMENT METHOD"
            )
          }
          className="h-12"
          icon={PaymentIcon} 
          onClick={handlePayNow}
          disabled={!paymentMethod || isProcessing || isButtonDisabled}
        />
      )}
    </div>
  );
};

export default PaymentDetailsSection;
